import React, { useState, useEffect } from "react";
import RefractorTable from "./refractorTable";
import { useForm } from "react-hook-form";
import AddButtonSection from '../../components/common/AddButtonSection';
import { commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import { showToast } from '../../components/common/toast';
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";

export default function GlassesPrescription({ patientVisitId }) {
  const {
    formState: {dirtyFields },
    register,
    handleSubmit,
    setValue,
    watch,
    reset
  } = useForm({
    defaultValues: {
      // Initialize all fields with empty strings
      L_Sph0Dry_Refraction: '',
      L_Vision0Dry_Refraction: '',
      L_NeCylar0Dry_Refraction: '',
      L_Axis0Dry_Refraction: '',
      L_Sph1Dry_Refraction: '',
      L_Vision1Dry_Refraction: '',
      L_NeCylar1Dry_Refraction: '',
      L_Axis1Dry_Refraction: '',
      R_Sph0Dry_Refraction: '',
      R_Vision0Dry_Refraction: '',
      R_NeCylar0Dry_Refraction: '',
      R_Axis0Dry_Refraction: '',
      R_Sph1Dry_Refraction: '',
      R_Vision1Dry_Refraction: '',
      R_NeCylar1Dry_Refraction: '',
      R_Axis1Dry_Refraction: ''
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  const headers = {
    "Content-Type": "application/json",
  };

  const fields = [
    { name: "L_Sph0Dry_Refraction", index: "distance_sph" },
    { name: "L_Vision0Dry_Refraction", index: "distance_vision" },
    { name: "L_NeCylar0Dry_Refraction", index: "distance_necylar" },
    { name: "L_Axis0Dry_Refraction", index: "distance_axis" },
    { name: "L_Sph1Dry_Refraction", index: "near_sph" },
    { name: "L_Vision1Dry_Refraction", index: "near_vision" },
    { name: "L_NeCylar1Dry_Refraction", index: "near_necylar" },
    { name: "L_Axis1Dry_Refraction", index: "near_axis" },
    { name: "R_Sph0Dry_Refraction", index: "distance_sph" },
    { name: "R_Vision0Dry_Refraction", index: "distance_vision" },
    { name: "R_NeCylar0Dry_Refraction", index: "distance_necylar" },
    { name: "R_Axis0Dry_Refraction", index: "distance_axis" },
    { name: "R_Sph1Dry_Refraction", index: "near_sph" },
    { name: "R_Vision1Dry_Refraction", index: "near_vision" },
    { name: "R_NeCylar1Dry_Refraction", index: "near_necylar" },
    { name: "R_Axis1Dry_Refraction", index: "near_axis" },
  ];

  const onSubmit = async (data) => {
    if (loading) return;
    
    setLoading(true);
    
    try {
      const eyeExamination = {
        patient_visit_id: patientVisitId,
        eyeTest: [
          {
            eye_type: "Left Eye",
            distance_axis: data?.L_Axis0Dry_Refraction || null,
            distance_necylar: data?.L_NeCylar0Dry_Refraction || null,
            distance_sph: data?.L_Sph0Dry_Refraction || null,
            distance_vision: data?.L_Vision0Dry_Refraction || null,
            near_axis: data?.L_Axis1Dry_Refraction || null,
            near_necylar: data?.L_NeCylar1Dry_Refraction || null,
            near_sph: data?.L_Sph1Dry_Refraction || null,
            near_vision: data?.L_Vision1Dry_Refraction || null,
          },
          {
            eye_type: "Right Eye",
            distance_axis: data?.R_Axis0Dry_Refraction || null,
            distance_necylar: data?.R_NeCylar0Dry_Refraction || null,
            distance_sph: data?.R_Sph0Dry_Refraction || null,
            distance_vision: data?.R_Vision0Dry_Refraction || null,
            near_axis: data?.R_Axis1Dry_Refraction || null,
            near_necylar: data?.R_NeCylar1Dry_Refraction || null,
            near_sph: data?.R_Sph1Dry_Refraction || null,
            near_vision: data?.R_Vision1Dry_Refraction || null,
          }
        ]
      };

      const response = await commonPostApi(API_ENDPOINTS.EDIT_EYE_EXAMINATION_DETAILS, eyeExamination, {
        headers,
      });

      if (response?.data?.success === true) {
        showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.EXAMINATION.GLASSES_UPDATED);
        // Refresh data after successful save
        await setGlassPrescription();
      } else {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.EYE_EXAM_REQUIRED_FIRST);
      }
    } catch (err) {
      console.error("Save error:", err); // Debug log
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const discard = () => {
    // Reset form to initial loaded values or empty values
    if (initialDataLoaded) {
      setGlassPrescription(); // Reload original data
    } else {
      reset(); // Reset to default empty values
    }
  };

  const submitButtons = [
    {
      type: "primarySuccessButton",
      label: loading ? "Saving..." : "Save Changes",
      class: "md:w-11rem md:pr-3 w-8rem mr-2",
      onClick: handleSubmit(onSubmit),
      disabled: loading
    },
    {
      type: "secondaryButton",
      label: "Discard",
      class: "w-7rem",
      onClick: discard,
      disabled: loading
    },
  ];

  const setGlassPrescription = async () => {
    if (!patientVisitId) {
      return;
    }

    try {
      const requestData = { patient_visit_id: patientVisitId };

      const response = await commonPostApi(API_ENDPOINTS.SEARCH_EYE_EXAMINATION_DETAILS, requestData, { headers });

      const formData = response?.data?.data;
      
      if (formData && Array.isArray(formData) && formData.length > 0) {
        const leftEyeData = formData.find(item => item.eye_type === "Left Eye");
        const rightEyeData = formData.find(item => item.eye_type === "Right Eye");

        // Set form values
        fields.forEach(field => {
          const eyeData = field.name.startsWith('L_') ? leftEyeData : rightEyeData;
          const value = eyeData ? eyeData[field.index] : '';
          setValue(field.name, value || '', { shouldDirty: false });
        });

        setInitialDataLoaded(true);
      } else {
        // No existing data, reset form to empty values
        reset();
        setInitialDataLoaded(false);
      }
    } catch (err) {
      console.error("Fetch error:", err); // Debug log
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_LOAD_DATA);
      setInitialDataLoaded(false);
    }
  };

  useEffect(() => {
    if (patientVisitId) {
      setGlassPrescription();
    }
  }, [patientVisitId]);

  // Debug: Watch form values
  const watchedValues = watch();
  useEffect(() => {
  }, [watchedValues]);

  return (
    <div>
      <div className="">
        <RefractorTable
          contentTable={[
            { head: "L/DO Dry Refraction", eye: "L" },
            { head: "R/DO Dry Refraction", eye: "R" },
          ]}
          register={register}
        />
      </div>
      <div className="mt-6 ml-2">
        <AddButtonSection 
          disabled={Object.keys(dirtyFields).length === 0 || loading} 
          buttons={submitButtons} 
        />
      </div>
    </div>
  );
}